// Browser-side image resizing so uploads arrive in sensible sizes (Supabase's
// free plan has no on-the-fly image transforms).

export const COVER_WIDTHS = [480, 960, 1600];
export const INLINE_MAX_WIDTH = 1600;
export const IMAGE_INPUT_MAX_BYTES = 20 * 1024 * 1024;
const OUTPUT_TYPE = 'image/webp';
const OUTPUT_QUALITY = 0.82;

export function canResize(file) {
    // Animated GIFs and SVGs would lose animation / vector quality.
    return Boolean(file?.type?.startsWith('image/')) && !['image/gif', 'image/svg+xml'].includes(file.type);
}

// Widths to generate for a source of `sourceWidth` pixels (never upscale).
export function targetWidths(sourceWidth, widths = COVER_WIDTHS) {
    const smaller = widths.filter((width) => width < sourceWidth);
    return smaller.length === widths.length ? smaller : [...smaller, Math.min(sourceWidth, widths[widths.length - 1])];
}

async function loadBitmap(file) {
    if (typeof createImageBitmap === 'function') return createImageBitmap(file);
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('This image could not be read.')); };
        img.src = url;
    });
}

function drawToBlob(bitmap, width) {
    const scale = width / bitmap.width;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve({ blob, width: canvas.width, height: canvas.height }) : reject(new Error('Image could not be resized.'))), OUTPUT_TYPE, OUTPUT_QUALITY);
    });
}

function safeBaseName(name) {
    return (name || 'image').replace(/\.[a-z0-9]+$/i, '').replace(/[^a-zA-Z0-9._-]/g, '-').slice(-60) || 'image';
}

async function uploadBlob(supabase, path, blob, contentType) {
    const { data, error } = await supabase.storage.from('images').upload(path, blob, {
        cacheControl: '31536000',
        upsert: false,
        contentType,
    });
    if (error) throw error;
    return supabase.storage.from('images').getPublicUrl(data.path).data.publicUrl;
}

// Uploads a cover in several widths. Returns { imageURL, variants } where
// variants = { width, height, sources: { "480": url, ... } } (or null for GIF/SVG).
export async function uploadCoverImage(supabase, userId, file, now = Date.now()) {
    const base = `blogs/${userId}/${now}-${safeBaseName(file.name)}`;
    if (!canResize(file)) {
        const imageURL = await uploadBlob(supabase, `${base}.${file.name.split('.').pop()}`, file, file.type);
        return { imageURL, variants: null };
    }
    const bitmap = await loadBitmap(file);
    const sources = {};
    let largest = null;
    for (const width of targetWidths(bitmap.width)) {
        const resized = await drawToBlob(bitmap, width);
        sources[resized.width] = await uploadBlob(supabase, `${base}-${resized.width}w.webp`, resized.blob, OUTPUT_TYPE);
        largest = resized;
    }
    bitmap.close?.();
    return {
        imageURL: sources[largest.width],
        variants: { width: largest.width, height: largest.height, sources },
    };
}

// Single downscaled upload for images placed inside the post body.
export async function uploadInlineImage(supabase, userId, file, now = Date.now()) {
    const base = `blogs/${userId}/${now}-${safeBaseName(file.name)}`;
    if (!canResize(file)) return uploadBlob(supabase, `${base}.${file.name.split('.').pop()}`, file, file.type);
    const bitmap = await loadBitmap(file);
    const resized = await drawToBlob(bitmap, Math.min(bitmap.width, INLINE_MAX_WIDTH));
    bitmap.close?.();
    return uploadBlob(supabase, `${base}-${resized.width}w.webp`, resized.blob, OUTPUT_TYPE);
}

// Turns stored variants into <img> srcSet/width/height props.
export function getResponsiveImageProps(variants) {
    const sources = variants?.sources;
    if (!sources || typeof sources !== 'object') return {};
    const entries = Object.entries(sources)
        .map(([width, url]) => [Number(width), url])
        .filter(([width, url]) => width > 0 && typeof url === 'string')
        .sort((a, b) => a[0] - b[0]);
    if (!entries.length) return {};
    return {
        srcSet: entries.map(([width, url]) => `${url} ${width}w`).join(', '),
        width: Number(variants.width) || undefined,
        height: Number(variants.height) || undefined,
    };
}
